"use client";

import Image from "next/image";
import { useState } from "react";
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";

import type { HomeDictionary } from "@/content/home";

interface AboutProps {
  copy: HomeDictionary["about"];
}

export default function About({ copy }: AboutProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const currentValue = copy.values[currentIndex];
  const viewport = { once: true, amount: 0.2 };

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % copy.values.length);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + copy.values.length) % copy.values.length);

  return (
    <LazyMotion features={domAnimation}>
    <section id="about" className="pt-24 bg-white overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-32 pt-8 lg:pt-16">
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: -34 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={viewport}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="flex flex-col justify-center lg:pr-8"
          >
            <div className="flex items-center mb-6">
              <div className="w-8 h-[2px] bg-terracotta mr-4" />
              <span className="text-terracotta font-bold text-[10px] uppercase tracking-[0.3em]">
                {copy.eyebrow}
              </span>
            </div>

            <h2 className="font-montserrat font-black text-5xl md:text-6xl text-petroleum uppercase leading-[0.95] tracking-tight mb-8">
              {copy.titleTop} <br />
              <span className="text-slate-400">{copy.titleAccent}</span>
            </h2>

            <div className="font-inter text-slate-800 text-lg leading-relaxed mb-8 space-y-4">
              <p>
                <span className="font-bold text-petroleum">Elite Petroleum Operations</span> {copy.introLead}
              </p>
              <p className="text-slate-600 font-light border-l-2 border-terracotta pl-6">
                {copy.introBody}
              </p>
            </div>

            <div className="flex flex-col space-y-4 border-l-2 border-gray-200 pl-6 mb-10">
              {copy.bullets.map((item) => (
                <div key={item} className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-4" />
                  <span className="font-montserrat font-bold text-sm text-petroleum uppercase tracking-wider">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <div>
              <a
                href="#soluciones"
                className="inline-block bg-terracotta text-white hover:bg-petroleum px-8 py-4 font-montserrat font-bold text-xs uppercase tracking-[0.2em] transition-colors duration-300 group shadow-lg"
              >
                {copy.cta}
                <span className="inline-block ml-2 group-hover:translate-x-1 transition-transform">
                  &rarr;
                </span>
              </a>
            </div>
          </m.div>

          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={{ duration: 0.7, delay: shouldReduceMotion ? 0 : 0.12, ease: "easeOut" }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="col-span-2 h-[220px] md:h-[260px] bg-gray-100 overflow-hidden relative group">
              <Image
                src="/images/aboutxd.jpeg"
                alt={copy.aboutAlt}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                quality={70}
                className="object-cover grayscale-[20%] group-hover:scale-105 transition-transform duration-700 ease-out"
              />
            </div>

            <div className="col-span-1 h-[160px] md:h-[180px] bg-gray-100 overflow-hidden relative group hidden sm:block">
              <Image
                src="/images/about2.webp"
                alt={copy.aboutDetailAlt}
                fill
                sizes="(max-width: 1024px) 50vw, 25vw"
                quality={65}
                className="object-cover grayscale-[20%] group-hover:scale-105 transition-transform duration-700 ease-out"
              />
            </div>

            <div className="col-span-2 sm:col-span-1 h-[160px] md:h-[180px] bg-petroleum p-6 flex flex-col justify-center border-t-4 border-terracotta relative overflow-hidden text-white">
              <span className="absolute -right-4 -bottom-4 font-montserrat font-black text-[100px] leading-none text-white/5 pointer-events-none">
                E
              </span>
              <div className="relative z-10">
                <span className="font-montserrat font-black text-white text-3xl lg:text-4xl block mb-1">100%</span>
                <span className="text-terracotta text-[9px] font-bold uppercase tracking-widest block">
                  {copy.technicalFocusLabel}
                </span>
                <div className="w-full h-px bg-white/10 my-3" />
                <span className="font-montserrat font-black text-white text-xl lg:text-2xl block mb-1">Elite</span>
                <span className="text-gray-400 text-[9px] font-bold uppercase tracking-widest block">
                  {copy.operationalStandardLabel}
                </span>
              </div>
            </div>
          </m.div>
        </div>
      </div>

      <div className="w-full mb-32 border-y border-gray-200 bg-white flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200 border-b border-gray-200">
          <div className="h-[350px] lg:h-auto relative overflow-hidden group">
            <Image
              src="/images/mision2.jpeg"
              alt={copy.missionAlt}
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              quality={60}
              className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
            />
          </div>
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={viewport}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="p-10 lg:p-16 flex flex-col justify-center group"
          >
            <h3 className="font-montserrat font-black text-3xl lg:text-4xl text-petroleum uppercase mb-2">
              {copy.missionTitle}
            </h3>
            <div className="w-12 h-1 bg-terracotta mb-8 transition-all group-hover:w-24" />
            <p className="font-inter text-slate-700 text-lg lg:text-xl font-light leading-relaxed">
              {copy.missionBody}
            </p>
          </m.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={viewport}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="p-10 lg:p-16 flex flex-col justify-center group order-2 lg:order-1"
          >
            <h3 className="font-montserrat font-black text-3xl lg:text-4xl text-petroleum uppercase mb-2">
              {copy.visionTitle}
            </h3>
            <div className="w-12 h-1 bg-terracotta mb-8 transition-all group-hover:w-24" />
            <p className="font-inter text-slate-700 text-lg lg:text-xl font-light leading-relaxed">
              {copy.visionBody}
            </p>
          </m.div>
          <div className="h-[350px] lg:h-auto relative overflow-hidden group order-1 lg:order-2">
            <Image
              src="/images/vision2.jpeg"
              alt={copy.visionAlt}
              fill
              sizes="(max-width: 1024px) 100vw, 50vw"
              quality={60}
              className="object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
            />
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col lg:flex-row bg-petroleum">
        <div className="w-full lg:w-1/2 h-[400px] lg:h-auto relative">
          <Image
            src="/images/texture.webp"
            alt={copy.textureAlt}
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            quality={55}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-petroleum" />
        </div>
        <m.div
          initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full lg:w-1/2 p-10 lg:p-24 flex flex-col justify-center"
        >
          <h3 className="font-montserrat font-bold text-terracotta uppercase text-xs tracking-[0.3em] mb-8">
            {copy.businessEyebrow}
          </h3>
          <p className="font-inter text-white text-2xl md:text-3xl lg:text-4xl leading-tight font-light mb-12 opacity-90">
            {copy.businessBody}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 border-t border-white/10 pt-8 text-white">
            {copy.drivers.map((driver) => (
              <div key={driver.label}>
                <span className="block text-white/40 text-[10px] uppercase mb-2">{driver.label}</span>
                <p className="font-bold text-sm uppercase tracking-wider">{driver.value}</p>
              </div>
            ))}
          </div>
        </m.div>
      </div>

      <div className="w-full bg-petroleum pt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-12">
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: -34 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={viewport}
            transition={{ duration: 0.65, ease: "easeOut" }}
          >
            <h2 className="font-montserrat font-black text-4xl lg:text-6xl text-white uppercase mb-6">
              {copy.valuesTitle} <span className="text-terracotta">{copy.valuesAccent}</span>
            </h2>
            <p className="font-inter text-lg text-white/70 border-l-2 border-terracotta pl-6 max-w-2xl">
              {copy.valuesBody}
            </p>
          </m.div>
        </div>

        <div className="w-full relative h-[600px] lg:h-[700px] overflow-hidden border-t border-white/10">
          <div className="absolute inset-0 w-full h-full bg-black">
            <AnimatePresence mode="wait">
              <m.div
                key={currentValue.id}
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 1.01 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="absolute inset-0"
              >
                <Image
                  src={currentValue.image}
                  alt={currentValue.title}
                  fill
                  sizes="100vw"
                  quality={55}
                  className="w-full h-full object-cover brightness-[0.7]"
                />
              </m.div>
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/20 to-transparent" />
          </div>

          <div className="relative z-10 h-full flex items-center px-6 lg:px-24">
            <AnimatePresence mode="wait">
              <m.div
                key={currentValue.id}
                initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 34 }}
                animate={{ opacity: 1, y: 0 }}
                exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -26 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="max-w-2xl border-l-4 border-terracotta pl-6 md:pl-10"
              >
                <span className="block font-mono text-terracotta mb-4 tracking-[0.3em] uppercase text-sm">
                  {currentValue.id}
                </span>
                <h3 className="font-montserrat font-black text-4xl md:text-5xl lg:text-6xl text-white mb-6 uppercase leading-tight">
                  {currentValue.title}
                </h3>
                <p className="font-inter font-light text-lg md:text-xl text-white/80 leading-relaxed">
                  {currentValue.description}
                </p>
              </m.div>
            </AnimatePresence>
          </div>

          <div className="absolute bottom-0 right-0 flex items-center bg-petroleum/95 backdrop-blur-md z-20 border-t border-l border-white/10">
            <div className="text-white font-mono text-sm px-10 hidden sm:block">
              <span className="text-terracotta">{currentIndex + 1}</span> / {copy.values.length}
            </div>
            <button
              onClick={prevSlide}
              className="p-8 hover:bg-terracotta text-white transition-all border-l border-white/10"
              aria-label={copy.previousLabel}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextSlide}
              className="p-8 bg-terracotta hover:bg-white text-white hover:text-terracotta transition-all"
              aria-label={copy.nextLabel}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
    </LazyMotion>
  );
}
